import React from 'react';
import { IPermission } from '../../auth/permissions';

export const formatColors = ['None', 'Green', 'Yellow', 'Red'] as const;

export type FormatColor = (typeof formatColors)[number];

export const badgeScanSelections = ['badgeId', 'employeeId'] as const;

export type BadgeScanSelection = (typeof badgeScanSelections)[number];

export type TabConfig = {
  key: string;
  label: string;
  permission?: IPermission | IPermission[];
  component: React.JSX.Element;
  extraCheck?: () => boolean;
};

export type UseTabManagerOutput = {
  activeTabKey: string | undefined;
  handleTabChange: (event: React.ChangeEvent<{}>, newValue: string) => void;
  filteredTabs: TabConfig[];
  renderTabContent: () => React.JSX.Element;
};

export type BarData = {
  x: string;
  y: number;
  tooltip: string;
  color?: string;
};

export type LineData = {
  x: string;
  y: number;
};

/**
 * Types shared by CSV export on `GridTableDefault` (legacy) and `GridTableDefault2` (MRT).
 *
 * Kept framework-agnostic on purpose: `AdditionalExportColumn` does **not** reference
 * `@devexpress/dx-react-grid`; it only mirrors the fields that the legacy merge logic used
 * (`name`, `title`, optional `getCellValue`, `position`) so existing callers stay compatible.
 */

/**
 * A column that appears in the downloaded CSV but is **not** part of the grid’s column definitions.
 *
 * **Usage:** Pass an array as `additionalExportColumns` on `GridTableDefault` or `GridTableDefault2`.
 * Each entry is spliced into the export column list at `position` (after sorting by `position`),
 * so you can inject derived fields (e.g. `row.mCell?.code`) next to normal columns.
 *
 * - **`name`:** Property used for the CSV cell and header key; if `getCellValue` is omitted, the value is read from `row[name]`.
 * - **`getCellValue`:** Optional; when set, its return value is exported for every row (same idea as the legacy grid’s per-column getter).
 */
export type AdditionalExportColumn = {
  name: string;
  title?: string;
  position: number;
  /** If set, export uses this; else `row[name]`. Parameters use `any` so callers can type `row` as their row DTO (e.g. `JobStation`). */
  getCellValue?: (row: any, columnName: string) => any;
};
